import {
	InvoiceRecipientType,
	useGetAllOrganizationUserQuery,
	useGetOrganizationSettingQuery,
	useSetOrganizationInvoiceRecipientsMutation,
	type OrganizationInvoiceRecipientInput,
} from '@codezo/graphql';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

/**
 * Who receives this client's invoices.
 *
 * Two kinds in one list: a system user of the client, and a bare external
 * address — their bookkeeper — who is not a user and never will be.
 *
 * ── The list is saved whole ──
 *
 * `setOrganizationInvoiceRecipients` takes the complete list, not a patch, so
 * every add and remove sends all of them. Local state is only updated once the
 * mutation confirms: an optimistic list that then failed would show a recipient
 * who is not actually going to receive anything, which is the one mistake this
 * section must not make.
 */

export interface LocalRecipient {
	key: string;
	type: InvoiceRecipientType;
	userId?: string;
	email?: string;
	name?: string;
}

const EMAIL_RE = /^\S+@\S+\.\S+$/;

let keyCounter = 0;
const nextKey = () => `r_${keyCounter++}`;

const toInput = (r: LocalRecipient): OrganizationInvoiceRecipientInput =>
	r.type === InvoiceRecipientType.User
		? { type: InvoiceRecipientType.User, userId: r.userId, name: r.name }
		: { type: InvoiceRecipientType.Email, email: r.email, name: r.name };

export function useInvoiceRecipients(organizationId: string) {
	const {
		data: settingData,
		loading: settingLoading,
		refetch: refetchSetting,
	} = useGetOrganizationSettingQuery({
		variables: { organizationId },
		fetchPolicy: 'no-cache',
	});

	const { data: usersData } = useGetAllOrganizationUserQuery({
		variables: { input: { organizationId, pagination: { currentPage: 1, limit: 200 } } },
		fetchPolicy: 'no-cache',
	});

	const orgUsers = useMemo(() => usersData?.getAllOrganizationUser?.data ?? [], [usersData]);
	const [saveRecipients, { loading: saving }] = useSetOrganizationInvoiceRecipientsMutation();

	const [recipients, setRecipients] = useState<LocalRecipient[]>([]);
	const [removingKey, setRemovingKey] = useState<string | null>(null);

	useEffect(() => {
		const list = settingData?.getOrganizationSetting?.invoiceRecipients ?? [];
		setRecipients(
			list.map((r) => ({
				key: nextKey(),
				type: r.type,
				userId: r.userId ?? undefined,
				email: r.email ?? undefined,
				name: r.name ?? undefined,
			}))
		);
	}, [settingData]);

	/** A recipient stored as a user id resolves to their current name and email,
	 * so a renamed user is not still listed under the name they had when added. */
	const userLabel = (userId?: string, fallback?: string) => {
		const u = orgUsers.find((ou) => ou.id === userId);
		if (u) {
			const name = `${u.firstName} ${u.lastName}`.trim();
			return {
				name: name || u.authenticationUser?.email || 'User',
				email: u.authenticationUser?.email,
			};
		}
		return { name: fallback || 'User', email: undefined };
	};

	/* Already-added users drop out of the picker rather than being added twice. */
	const availableUsers = useMemo(
		() =>
			orgUsers.filter(
				(u) =>
					!recipients.some(
						(r) => r.type === InvoiceRecipientType.User && r.userId === u.id
					)
			),
		[orgUsers, recipients]
	);

	const userOptions = availableUsers.map((u) => ({
		value: u.id,
		label: `${u.firstName} ${u.lastName}`.trim() || u.authenticationUser?.email || 'User',
	}));

	const persist = async (next: LocalRecipient[]): Promise<boolean> => {
		try {
			const result = await saveRecipients({
				variables: { input: { organizationId, recipients: next.map(toInput) } },
			});
			if (result.data?.setOrganizationInvoiceRecipients.success) {
				setRecipients(next);
				refetchSetting();
				return true;
			}
			toast.error(
				result.data?.setOrganizationInvoiceRecipients.message || 'Failed to update'
			);
			return false;
		} catch {
			toast.error('Failed to update invoice recipients');
			return false;
		}
	};

	const addUser = async (userId: string) => {
		const u = orgUsers.find((ou) => ou.id === userId);
		if (!u) return false;
		const name = `${u.firstName} ${u.lastName}`.trim() || undefined;
		const ok = await persist([
			...recipients,
			{ key: nextKey(), type: InvoiceRecipientType.User, userId: u.id, name },
		]);
		if (ok) toast.success('Recipient added');
		return ok;
	};

	const addEmail = async (email: string, name?: string) => {
		const ok = await persist([
			...recipients,
			{
				key: nextKey(),
				type: InvoiceRecipientType.Email,
				email: email.trim(),
				name: name?.trim() || undefined,
			},
		]);
		if (ok) toast.success('Recipient added');
		return ok;
	};

	const remove = async (key: string) => {
		setRemovingKey(key);
		const ok = await persist(recipients.filter((r) => r.key !== key));
		if (ok) toast.success('Recipient removed');
		setRemovingKey(null);
	};

	/** Validates an address against the format AND the list it is joining. */
	const validateEmail = (value: string): string | null => {
		const trimmed = value.trim();
		if (trimmed === '') return null;
		if (!EMAIL_RE.test(trimmed)) return 'Enter a valid email address';
		const duplicate = recipients.some(
			(r) =>
				r.type === InvoiceRecipientType.Email &&
				r.email?.toLowerCase() === trimmed.toLowerCase()
		);
		return duplicate ? 'That email is already added' : null;
	};

	const canAddEmail = (value: string) =>
		EMAIL_RE.test(value.trim()) && validateEmail(value) === null;

	return {
		loading: settingLoading,
		saving,
		recipients,
		removingKey,
		userLabel,
		userOptions,
		addUser,
		addEmail,
		remove,
		validateEmail,
		canAddEmail,
	};
}
